import type { TranscriptEntry } from '../hooks/useVapi';

export type Speaker = 'assistant' | 'user' | 'system';

export interface SavedTranscriptEntry {
  id?: string | number;
  speaker?: string;
  role?: string;
  text?: string;
  message?: string;
  transcript?: string;
  timestamp?: string | number | Date;
}

export interface NormalizedTranscriptEntry {
  id: string;
  speaker: Speaker;
  text: string;
  timestamp: string;
}

export type TranscriptInput = Array<TranscriptEntry | SavedTranscriptEntry> | null | undefined;

export function normalizeSpeaker(value: string | undefined): Speaker {
  if (value === 'assistant') {
    return 'assistant';
  }

  if (value === 'user') {
    return 'user';
  }

  return 'system';
}

export function normalizeTimestamp(value: string | number | Date | undefined): string {
  if (!value) {
    return new Date().toISOString();
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString();
  }

  return parsed.toISOString();
}

export function normalizeTranscript(
  entries: TranscriptInput
): NormalizedTranscriptEntry[] {
  if (!entries || entries.length === 0) {
    return [];
  }

  return entries
    .map((entry, index) => {
      const speakerValue =
        ('role' in entry && typeof entry.role === 'string' && entry.role) ||
        ('speaker' in entry && typeof entry.speaker === 'string' && entry.speaker) ||
        undefined;
      const speaker = normalizeSpeaker(speakerValue);
      const text =
        ('text' in entry && typeof entry.text === 'string' && entry.text) ||
        ('message' in entry && typeof entry.message === 'string' && entry.message) ||
        ('transcript' in entry && typeof entry.transcript === 'string' && entry.transcript) ||
        '';

      if (!text.trim()) {
        return null;
      }

      const rawId =
        ('id' in entry && entry.id !== undefined ? String(entry.id) : null) ||
        `${speaker}-${index}`;

      return {
        id: rawId,
        speaker,
        text: text.trim(),
        timestamp: normalizeTimestamp(
          'timestamp' in entry ? entry.timestamp : undefined
        ),
      } satisfies NormalizedTranscriptEntry;
    })
    .filter((entry): entry is NormalizedTranscriptEntry => entry !== null);
}

export const speakerStyles: Record<Speaker, string> = {
  assistant: 'border-kaiva-success/30 bg-kaiva-teal/10 text-kaiva-teal',
  user: 'border-kaiva-coral/30 bg-kaiva-coral/10 text-kaiva-coral',
  system: 'border-border bg-muted text-foreground',
};
